// src/components/appSettings/Banner/BannerList.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPlus,
  FiFilter,
  FiDownload,
  FiUpload,
  FiRefreshCw,
  FiGrid,
  FiList,
  FiCalendar,
  FiMapPin,
} from "react-icons/fi";
import {
  fetchAllBannersThunk,
  deleteBannerThunk,
  updateBannerStatusThunk,
  reorderBannersThunk,
  importBannersThunk,
  exportConfigThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

const BannerList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { banners, loadingList, error } = useSelector(
    (state) => state.appSettings
  );
  const { user } = useSelector((state) => state.auth);
  const { selectedBranch, branches } = useSelector((state) => state.branch);

  // Local state
  const [viewMode, setViewMode] = useState("grid");
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Filters state - Initialize with selectedBranch
  const [filters, setFilters] = useState({
    search: "",
    isActive: "",
    branchCode: selectedBranch || "",
    position: "",
    validFrom: "",
    validUntil: "",
  });

  // Update filters when selectedBranch changes
  useEffect(() => {
    if (selectedBranch) {
      setFilters((prev) => ({
        ...prev,
        branchCode: selectedBranch,
      }));
    }
  }, [selectedBranch]);

  // Load banners function
  const loadBanners = useCallback(() => {
    // Prepare filter params - only send non-empty values
    const filterParams = {};

    if (filters.isActive !== "") {
      filterParams.isActive = filters.isActive;
    }

    if (filters.branchCode) {
      filterParams.branchCode = filters.branchCode;
    }

    // Note: search, position, validFrom, validUntil need backend support
    // Add them here once backend is updated
    if (filters.search) {
      filterParams.search = filters.search;
    }

    if (filters.position) {
      filterParams.position = filters.position;
    }

    if (filters.validFrom) {
      filterParams.validFrom = filters.validFrom;
    }

    if (filters.validUntil) {
      filterParams.validUntil = filters.validUntil;
    }

    dispatch(fetchAllBannersThunk(filterParams));
  }, [dispatch, filters]);

  // Load banners on mount and when filters change
  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  // Handlers
  const handleDeleteBanner = async (bannerId) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      try {
        await dispatch(deleteBannerThunk(bannerId)).unwrap();
        toast.success("Banner deleted successfully");
        loadBanners();
      } catch (error) {
        toast.error(error.message || "Failed to delete banner");
      }
    }
  };

  const handleToggleStatus = async (bannerId, currentStatus) => {
    try {
      await dispatch(
        updateBannerStatusThunk({
          id: bannerId,
          statusData: { isActive: !currentStatus },
        })
      ).unwrap();
      toast.success("Banner status updated successfully");
      loadBanners();
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleSelectBanner = (bannerId) => {
    setSelectedBanners((prev) =>
      prev.includes(bannerId)
        ? prev.filter((id) => id !== bannerId)
        : [...prev, bannerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBanners.length === banners.length) {
      setSelectedBanners([]);
    } else {
      setSelectedBanners(banners.map((b) => b._id));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      isActive: "",
      branchCode: selectedBranch || "",
      position: "",
      validFrom: "",
      validUntil: "",
    });
  };

  const handleImport = async (file) => {
    try {
      await dispatch(importBannersThunk(file)).unwrap();
      toast.success("Banners imported successfully");
      setShowImportModal(false);
      loadBanners();
    } catch (error) {
      toast.error(error.message || "Failed to import banners");
    }
  };

  const handleExport = async () => {
    try {
      await dispatch(exportConfigThunk("banners")).unwrap();
      toast.success("Banners exported successfully");
    } catch (error) {
      toast.error(error.message || "Failed to export banners");
    }
  };

  const getBranchName = (branchCode) => {
    const branch = branches?.find((b) => b.code === branchCode);
    return branch ? branch.name : branchCode;
  };

  const getStatusColor = (banner) => {
    const now = new Date();
    const validFrom = new Date(banner.validFrom);
    const validUntil = new Date(banner.validUntil);

    if (!banner.isActive) return "gray";
    if (now < validFrom) return "yellow";
    if (now > validUntil) return "red";
    return "green";
  };

  const getStatusText = (banner) => {
    const now = new Date();
    const validFrom = new Date(banner.validFrom);
    const validUntil = new Date(banner.validUntil);

    if (!banner.isActive) return "Inactive";
    if (now < validFrom) return "Scheduled";
    if (now > validUntil) return "Expired";
    return "Active";
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Banner Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage home screen banners and promotional content
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={handleExport}>
            <FiDownload className="mr-2" />
            Export
          </Button>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <FiUpload className="mr-2" />
            Import
          </Button>
          <Button onClick={() => navigate("/app-settings/banners/new")}>
            <FiPlus className="mr-2" />
            Add Banner
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow p-3 md:p-4 lg:p-6 mb-6">
        {/* Main Filter Section */}
        <div className="space-y-4 mb-4">
          {/* Top Row: Search and Actions */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            {/* Search Input */}
            <input
              type="text"
              placeholder="Search banners..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
            />

            {/* Right Side Actions */}
            <div className="flex items-center gap-2 justify-between sm:justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={loadBanners}
                disabled={loadingList}
                className="shrink-0"
                aria-label="Refresh banners"
              >
                <FiRefreshCw className={loadingList ? "animate-spin" : ""} />
              </Button>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1 shrink-0">
                <button
                  className={`p-1.5 rounded transition-all ${
                    viewMode === "grid"
                      ? "bg-white shadow"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                >
                  <FiGrid />
                </button>
                <button
                  className={`p-1.5 rounded transition-all ${
                    viewMode === "list"
                      ? "bg-white shadow"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => setViewMode("list")}
                  aria-label="List view"
                >
                  <FiList />
                </button>
              </div>
            </div>
          </div>

          {/* Filter Controls Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Status Select */}
            <select
              value={filters.isActive}
              onChange={(e) => handleFilterChange("isActive", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {/* Branch Select */}
            <select
              value={filters.branchCode}
              onChange={(e) => handleFilterChange("branchCode", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            >
              <option value="">All Branches</option>
              {branches?.map((branch) => (
                <option key={branch.code} value={branch.code}>
                  {branch.name} ({branch.code})
                </option>
              ))}
            </select>

            {/* More Filters Button */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="w-full"
            >
              <FiFilter className="mr-2" />
              More Filters
            </Button>

            {/* Clear Filters Button */}
            <Button
              variant="outline"
              onClick={handleClearFilters}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position
                </label>
                <select
                  value={filters.position}
                  onChange={(e) =>
                    handleFilterChange("position", e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
                >
                  <option value="">All Positions</option>
                  <option value="1">Position 1</option>
                  <option value="2">Position 2</option>
                  <option value="3">Position 3</option>
                  <option value="4">Position 4</option>
                  <option value="5">Position 5</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid From
                </label>
                <input
                  type="date"
                  value={filters.validFrom}
                  onChange={(e) =>
                    handleFilterChange("validFrom", e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid Until
                </label>
                <input
                  type="date"
                  value={filters.validUntil}
                  onChange={(e) =>
                    handleFilterChange("validUntil", e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
                />
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions Banner */}
        {selectedBanners.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-100">
            <span className="text-sm md:text-base text-blue-700 font-medium">
              {selectedBanners.length} banner
              {selectedBanners.length !== 1 ? "s" : ""} selected
            </span>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 sm:flex-none whitespace-nowrap"
              >
                Bulk Actions
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedBanners([])}
                className="flex-1 sm:flex-none whitespace-nowrap"
              >
                Clear Selection
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Banners Display */}
      {loadingList ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : banners.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FiCalendar className="mx-auto text-5xl text-gray-400 mb-4" />
          <p className="text-gray-500">No banners found</p>
          <Button
            className="mt-4"
            onClick={() => navigate("/app-settings/banners/new")}
          >
            Create Your First Banner
          </Button>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {banners.map((banner) => (
            <BannerCard
              key={banner._id}
              banner={banner}
              selected={selectedBanners.includes(banner._id)}
              onSelect={handleSelectBanner}
              onEdit={(id) => navigate(`/app-settings/banners/edit/${id}`)}
              onDelete={handleDeleteBanner}
              onToggleStatus={handleToggleStatus}
              onView={(id) => navigate(`/app-settings/banners/${id}`)}
              getStatusColor={getStatusColor}
              getStatusText={getStatusText}
              getBranchName={getBranchName}
            />
          ))}
        </div>
      ) : (
        <BannerListView
          banners={banners}
          selectedBanners={selectedBanners}
          onSelectAll={handleSelectAll}
          onSelect={handleSelectBanner}
          onEdit={(id) => navigate(`/app-settings/banners/edit/${id}`)}
          onDelete={handleDeleteBanner}
          onToggleStatus={handleToggleStatus}
          onView={(id) => navigate(`/app-settings/banners/${id}`)}
          getStatusColor={getStatusColor}
          getStatusText={getStatusText}
          getBranchName={getBranchName}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImport}
        />
      )}
    </div>
  );
};

// Banner Card Component
const BannerCard = ({
  banner,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onView,
  getStatusColor,
  getStatusText,
  getBranchName,
}) => {
  const statusColor = getStatusColor(banner);
  const statusText = getStatusText(banner);

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-all duration-200">
      <div className="relative">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(banner._id)}
          className="absolute top-2 left-2 z-10"
        />

        {/* Banner Image Preview */}
        <div className="h-48 bg-gray-200 rounded-t-lg overflow-hidden">
          {banner.images?.desktop ? (
            <img
              src={banner.images.desktop}
              alt={banner.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <FiCalendar className="text-4xl text-gray-400" />
            </div>
          )}
        </div>

        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <span
            className={`inline-flex px-2 py-1 text-xs rounded-full bg-${statusColor}-100 text-${statusColor}-800`}
          >
            {statusText}
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-2">
          <h3 className="font-medium text-gray-900 line-clamp-1">
            {banner.title}
          </h3>
          {banner.subtitle && (
            <p className="text-sm text-gray-500 line-clamp-1">
              {banner.subtitle}
            </p>
          )}
        </div>

        <div className="space-y-2 text-sm mb-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Position:</span>
            <span className="font-medium">{banner.displayRules?.position}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Priority:</span>
            <span className="font-medium">{banner.displayRules?.priority}</span>
          </div>
          {banner.targeting?.branches?.length > 0 && (
            <div className="flex items-start justify-between">
              <span className="text-gray-500">Branches:</span>
              <span className="font-medium text-right">
                {banner.targeting.branches.length === 1
                  ? getBranchName(banner.targeting.branches[0])
                  : `${banner.targeting.branches.length} branches`}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Valid Until:</span>
            <span className="font-medium">
              {new Date(banner.validUntil).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Analytics Quick View */}
        {banner.analytics && (
          <div className="grid grid-cols-3 gap-2 p-2 bg-gray-50 rounded-lg mb-3">
            <div className="text-center">
              <p className="text-xs text-gray-500">Impressions</p>
              <p className="text-sm font-semibold">
                {banner.analytics.impressions || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Clicks</p>
              <p className="text-sm font-semibold">
                {banner.analytics.clicks || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">CTR</p>
              <p className="text-sm font-semibold">
                {banner.analytics.impressions > 0
                  ? (
                      (banner.analytics.clicks / banner.analytics.impressions) *
                      100
                    ).toFixed(1)
                  : 0}
                %
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(banner._id)}
            title="Edit Banner"
          >
            <FiEdit2 />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onView(banner._id)}
            title="View Details"
          >
            <FiEye />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggleStatus(banner._id, banner.isActive)}
            className={banner.isActive ? "text-red-600" : "text-green-600"}
            title={banner.isActive ? "Deactivate" : "Activate"}
          >
            {banner.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(banner._id)}
            className="text-red-600 hover:bg-red-50"
            title="Delete Banner"
          >
            <FiTrash2 />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Banner List View Component
const BannerListView = ({
  banners,
  selectedBanners,
  onSelectAll,
  onSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onView,
  getStatusColor,
  getStatusText,
  getBranchName,
}) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left">
            <input
              type="checkbox"
              onChange={onSelectAll}
              checked={selectedBanners.length === banners.length}
            />
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Banner
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Position
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Branches
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Validity
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {banners.map((banner) => {
          const statusColor = getStatusColor(banner);
          const statusText = getStatusText(banner);

          return (
            <tr key={banner._id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedBanners.includes(banner._id)}
                  onChange={() => onSelect(banner._id)}
                />
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div className="h-10 w-16 bg-gray-200 rounded overflow-hidden mr-3">
                    {banner.images?.desktop && (
                      <img
                        src={banner.images.desktop}
                        alt={banner.title}
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {banner.title}
                    </div>
                    {banner.subtitle && (
                      <div className="text-sm text-gray-500">
                        {banner.subtitle}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  Pos: {banner.displayRules?.position}
                </div>
                <div className="text-sm text-gray-500">
                  Priority: {banner.displayRules?.priority}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {banner.targeting?.branches?.length > 0
                    ? banner.targeting.branches.length === 1
                      ? getBranchName(banner.targeting.branches[0])
                      : `${banner.targeting.branches.length} branches`
                    : "All branches"}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {new Date(banner.validFrom).toLocaleDateString()}
                </div>
                <div className="text-sm text-gray-500">
                  to {new Date(banner.validUntil).toLocaleDateString()}
                </div>
              </td>
              <td className="px-6 py-4">
                <span
                  className={`px-2 py-1 text-xs rounded-full bg-${statusColor}-100 text-${statusColor}-800`}
                >
                  {statusText}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(banner._id)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    onClick={() => onView(banner._id)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <FiEye />
                  </button>
                  <button
                    onClick={() => onDelete(banner._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// Import Modal Component
const ImportModal = ({ isOpen, onClose, onImport }) => {
  const [file, setFile] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    onImport(file);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Import Banners</h2>

        <div className="mb-4">
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files[0])}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-primary file:text-white
              hover:file:bg-primary-dark"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Import</Button>
        </div>
      </div>
    </div>
  );
};

export default BannerList;
